import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { Button, ButtonSizes } from 'atoms';
import { PATHS } from 'router/config';
import { usePendingSurveysCount } from 'core/hooks';

/**
 * Global survey nag (spec 0011). Shown on every LMS page while the student has
 * pending surveys — an intentionally persistent reminder (owner's decision),
 * mirroring the debtor AccessBlockBanner (spec 0003) but on the amber brand
 * surface instead of red: it nags, it does not alarm. Rides the cached `me`
 * query (`pendingSurveysCount`) — no dedicated request.
 */
export const SurveyNagBanner = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { count, activeRole } = usePendingSurveysCount();

  // Students only: a Teacher/Methodist session must not be nagged about
  // surveys addressed to the person's student role. Unlike the debtor banner
  // there is no null-role fallback — a positive count only ever comes from a
  // student `me`, and `role="status"` (not "alert") matches the softer tone.
  if (activeRole !== 'Student' || count <= 0) {
    return null;
  }

  return (
    <Banner role="status">
      <Title>
        {count === 1 ? t('surveys.banner.title') : t('surveys.banner.titleMany', { n: count })}
      </Title>
      <Text>{t('surveys.banner.text')}</Text>
      <TakeSurveyButton
        size={ButtonSizes.sm}
        variant="outline"
        onClick={() => navigate(PATHS.surveys)}>
        {t('surveys.banner.action')}
      </TakeSurveyButton>
    </Banner>
  );
});

// Amber/yellow via the brand tokens (Colors.main is the amber brand surface;
// btn_text is its designed contrast color — both dark-theme safe), deliberately
// distinct from the red debtor banner it sits next to.
const Banner = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: 0 auto 16px;
  padding: 20px 24px;
  border-radius: 12px;
  background-color: ${Colors.main};
  color: ${Colors.btn_text};
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 8px;
`;

const Text = styled.div`
  font-size: 14px;
  line-height: 1.4;
`;

const TakeSurveyButton = styled(Button)`
  && {
    width: auto;
    margin-top: 20px;
    background-color: ${Colors.white};
    color: ${Colors.black};
    border: none;
    font-weight: 600;

    &:hover {
      background-color: ${Colors.bg_gray};
    }
  }
`;
